import { Pagination, Popconfirm, Switch, Table } from 'antd';
import React, { ReactElement, ReactNode, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { APPLICATION_ID, authFeatureConstants, DEFAULT_PAGE_SIZE, PAGE_QUERY_PARAM, StatusFilterType } from '../../../../shared/constants/commonConstants';
import { INVENTORY_SAVE_IP_PACKAGE } from '../../../../shared/constants/urlEndPoints';
import { IPPackageDTO, PricingDetail, SaveInPatientPackageRequest, SaveInPatientPackageResponse } from '../../../../shared/models/inPatient.model';
import SendRequest from '../../../../shared/services/sendRequest.service';
import { base64UrlEncode, canActivate, error, extractSecondLevelBaseUrl, formatPrice, success } from '../../../../utils/utility';
import { ReactComponent as ApprovedIcon  } from '../../../../assets/images/svg/i_approved.svg';
import { ReactComponent as PendingIcon  } from '../../../../assets/images/svg/i_pending.svg';
import { ReactComponent as RejectedIcon  } from '../../../../assets/images/svg/i_rejected.svg';

const DaycarePackageList = ({ match: { path }, packages, currentPageNo, totalRecords, onPaginationEvent }: any): ReactElement => {
  const [packageList, setPackageList] = useState(null as any);
  const isAuthorized = canActivate(authFeatureConstants.EDIT_DAY_CARE_SERVICES);
  useEffect((): void => {
    setPackageList(packages);
  }, [packages]);

  const history = useHistory();
  const rowClick = (record: any): any => {
    history.push(extractSecondLevelBaseUrl(path) + '/daycare-package-detail/' +
      base64UrlEncode(record.key) + PAGE_QUERY_PARAM + currentPageNo);
  };

  const onChangeSwitch = (value: any, record: any): any => {
    const packageData = packageList.filter((item: any): any => item.id === record.key);
    if (!packageData) {
      return;
    }
    const payload: SaveInPatientPackageRequest = {
      applicationId: APPLICATION_ID,
      package: {
        ...packageData[0], isActive: !record.isActive, inclusionList: [], pricingList: [],
      },
    };
    onSavePackage(payload);
  };

  const onSavePackage = async (payload: SaveInPatientPackageRequest): Promise<void> => {
    const url = process.env.REACT_APP_INVENTORY_API_BASE_URL + INVENTORY_SAVE_IP_PACKAGE;
    const result: SaveInPatientPackageResponse = await SendRequest.instance.post<
      SaveInPatientPackageRequest,
      SaveInPatientPackageResponse
    >(url, payload);
    if (result && result.isSuccess) {
      const index = packageList.findIndex((item: any): any => item.id === payload.package.id);
      const newPackageList = [...packageList];
      packageList[index].isActive = payload.package.isActive;
      packageList[index].statusName = StatusFilterType.isSubmit;
      setPackageList(newPackageList);
      success(`Package ${payload.package.isActive ? 'enabled' : 'disabled'} successfully`);
    } else {
      error('Please try again');
    }
  };

  const dataSource: any[] | undefined = [];

  const columns = [
    {
      title: 'Active',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (e: any, record: any): any => (
        <div onClick={(event: any): void => event.stopPropagation()}>
          <Popconfirm
            disabled={!isAuthorized}
            title={`Do you want to ${record?.isActive ? 'stop ' : 'enable '} appointments ?`}
            onConfirm={(value: any): any => onChangeSwitch(value, record)}
            okText="Yes"
            cancelText="No"
          >
            <Switch size="small"
              disabled={!isAuthorized}
              checked={record?.isActive}
            />
          </Popconfirm>
        </div>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'packageName',
      key: 'packageName',
      className: 'mouse-pointer',
    },
    {
      title: 'Department',
      dataIndex: 'groupName',
      key: 'groupName',
    },
    {
      title: 'Sessions',
      dataIndex: 'packageSession',
      key: 'packageSession',
    },
    {
      title: 'Price Range',
      dataIndex: 'priceMinMax',
      key: 'priceMinMax',
    },
    {
      title: 'Status',
      dataIndex: 'packageStatus',
      key: 'packageStatus',
      render: (text: any, record: any): ReactNode => {
        switch (record.statusName) {
          case StatusFilterType.isApproved:
            return <div className="status verified" >
              <ApprovedIcon className="fill-approved v-align-sub" /> Verified
                </div>;
          case StatusFilterType.isSubmit:
            return <div className="status pending">
              <PendingIcon className="fill-pending v-align-sub" /> Pending
                </div>;
          case StatusFilterType.isRejected:
            return <div className="status rejected">
              <RejectedIcon className="fill-rejected v-align-sub" /> Rejected
                </div>;
          default:
            return <div className="status pending">
              <PendingIcon className="fill-approved v-align-sub" /> Pending
                </div>;
        }
      },
    },
  ];

  if (packageList) {
    packageList.map((dayCare: IPPackageDTO): void => {
      const price = dayCare?.pricingDetails?.length ? dayCare?.pricingDetails : null as any;
      const minMax = price?.map((val: any): void => val?.discountedPrice);
      let min = 0;
      let max = 0;
      if (minMax) {
        min = Math.min(...minMax);
        max = Math.max(...minMax);
      }
      const tableData = {
        key: dayCare.id,
        isActive: dayCare.isActive,
        packageName: dayCare.procedureName,
        groupName: dayCare.departmentName,
        packageStatus: dayCare.isApproved,
        priceMinMax: min === max ? formatPrice(min, false) : formatPrice(min, false) + ' - ' + formatPrice(max, false, false),
        packageSession: dayCare?.pricingDetails?.filter((session: PricingDetail): boolean => session.isActive).length,
        statusName: dayCare.statusName,
      };
      dataSource.push(tableData);
    });
  }

  return (
    <div>
      <Table columns={columns} dataSource={dataSource}
        pagination={false}
        onRow={(record: any, rowIndex: any): any => {
          return {
            onClick: (event: any): any => { rowClick(record); },
          };
        }} />
      {(dataSource && dataSource.length > 0 && totalRecords) &&
          <Pagination
            defaultCurrent={currentPageNo}
            defaultPageSize={DEFAULT_PAGE_SIZE}
            total={totalRecords} onChange={(page: number, pagesize: any): any => onPaginationEvent(page, pagesize)}
            className="mt-md text-center" />
      }
    </div>
  );
};

export default DaycarePackageList;
